// ─────────────────────────────────────────────────────────────
// ZIFEK Finance — exports PDF & Excel
// Réutilise l'architecture d'export existante (jspdf + xlsx).
// ─────────────────────────────────────────────────────────────
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';

export interface ExportTable {
  title: string;
  subtitle?: string;
  headers: string[];
  rows: (string | number)[][];
}

function safeName(name: string): string {
  return (name || 'export').trim().replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '-').toLowerCase();
}

/** Export CSV (avec BOM pour Excel). */
export function exportTableCsv(table: ExportTable, filename?: string): void {
  const { title, headers, rows } = table;
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName(filename || title)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Export Excel (.xlsx) via SheetJS. */
export function exportTableExcel(table: ExportTable, filename?: string): void {
  const { title, headers, rows } = table;
  const aoa: (string | number)[][] = [[title], [], headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, (title || 'Rapport').slice(0, 30));
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName(filename || title)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Export PDF texte (tableau) via jsPDF, avec pagination automatique. */
export function exportTablePdf(table: ExportTable, filename?: string): void {
  const { title, subtitle, headers, rows } = table;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;
  const usableW = pageW - margin * 2;
  const colW = usableW / Math.max(1, headers.length);
  let y = margin;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(title || 'Rapport', margin, y);
  y += 18;
  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(subtitle, margin, y);
    y += 14;
    doc.setTextColor(0);
  }
  y += 6;

  const drawHeader = () => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setFillColor(245, 245, 245);
    doc.rect(margin - 4, y - 11, usableW + 8, 16, 'F');
    headers.forEach((h, i) => doc.text(String(h), margin + i * colW, y));
    y += 16;
    doc.setFont('helvetica', 'normal');
  };

  drawHeader();

  doc.setFontSize(8.5);
  rows.forEach((row) => {
    if (y > pageH - margin) {
      doc.addPage();
      y = margin;
      drawHeader();
    }
    row.forEach((cell, i) => {
      const text = String(cell ?? '');
      const clipped = doc.splitTextToSize(text, colW - 6)[0] || '';
      doc.text(clipped, margin + i * colW, y);
    });
    y += 14;
  });

  doc.save(`${safeName(filename || title)}.pdf`);
}